// Polymarket US as a VenueEngine: PolymarketFeed frames become UiSnapshots; brackets are not ported yet.

import type { Engine } from '../core/engine';
import { getMeta, setMeta, type FillRow } from '../core/db';
import { MOBILE_ID, pnlTree } from '../core/accounts';
import { MC_SETTLE, MQ_PER_CONTRACT, type MC, type MQ } from '../core/money';
import type { AccountRow, LadderRow, OrdersOutRow, PnlSnapshot, PositionRow, SettledRow, UiOrder, UiSnapshot } from '../core/types';
import { AmbiguousPlacement, VenueAPIError } from '../venue/rest';
import { logError, logInfo, logWarn } from '../core/log';
import { journal } from '../core/journal';
import type { PmBbo, PmBook, PmEvent, PmExecution, PmMarket, PmOrder } from './client';
import { buildAccountLedgers, type PmMarketLedger } from './ledger';
import type { NormalizedOrder } from '../kalshi/client';
import { PolymarketFeed } from './feed';
import type { ExploreEvent, ExploreMarket, ExploreState, VenueEngine, VenueEvents, VenueHints } from '../venue/engine';

type Listener<K extends keyof VenueEvents> = (payload: VenueEvents[K]) => void;

const ORDER_STATES: Record<string, string> = {
  ORDER_STATE_NEW: 'resting',
  ORDER_STATE_PENDING_NEW: 'resting',
  ORDER_STATE_PENDING_RISK: 'resting',
  ORDER_STATE_PENDING_REPLACE: 'resting',
  ORDER_STATE_PENDING_CANCEL: 'resting',
  ORDER_STATE_PARTIALLY_FILLED: 'partially_filled',
  ORDER_STATE_FILLED: 'filled',
  ORDER_STATE_CANCELED: 'canceled',
  ORDER_STATE_CANCELLED: 'canceled',
  ORDER_STATE_REPLACED: 'canceled',
  ORDER_STATE_REJECTED: 'canceled',
  ORDER_STATE_EXPIRED: 'canceled',
};
const ACTIVE = new Set(['resting', 'partially_filled']);
/** Lines first, props last: what someone opening a game wants to see. */
const KIND_ORDER: Record<string, number> = { MONEYLINE: 0, SPREAD: 1, TOTAL: 2, DRAWABLE_OUTCOME: 3, FUTURE: 4, PROP: 9 };
/** How far back an open order may be to count as "the one we just sent". */
const AMBIGUOUS_WINDOW_MS = 60_000;

/** Which side of the YES ladder an order rests on. */
/** IndexedDB meta key for the saved order id -> account map. */
const ORDER_ACCOUNTS_KEY = 'pm_order_accounts_v1';

function ladderSide(o: PmOrder): 'y' | 'n' {
  return o.ladder;
}

export class PolymarketEngine implements VenueEngine {
  readonly venue = 'polymarket' as const;
  readonly label = 'Polymarket US';
  readonly tradingEnabled = true;
  readonly hints: VenueHints = {
    explore: 'Search events… (e.g. fed, 49ers) or paste a polymarket.us link',
    ladder: 'Search market… (slug or words)',
    exploreEmpty: 'Type to search Polymarket US events, or paste a polymarket.us link.',
  };
  readonly feed = new PolymarketFeed();

  /** Refcounted, like the Kalshi engine's: two ladders on one slug share one feed. */
  private subs = new Map<string, number>();
  private listeners = new Map<keyof VenueEvents, Set<Listener<any>>>();
  /** order id -> the app account it was placed under (the venue has no such notion); saved, since nothing else records it. */
  private orderAccounts = new Map<string, string>();
  private saveAccountsTimer: number | null = null;
  private provisionalSeq = 0;
  /** order id -> slug, for cancels; the venue wants both. */
  private orderSlugs = new Map<string, string>();
  /** Estimated shares ahead of each of our orders at its level, in MQ (the venue reports no true queue position). */
  private estAhead = new Map<string, MQ>();
  /** order id -> venue time (ms) accepted; a frame stamped earlier can't contain the order and mustn't be netted against it. */
  private orderAcceptedAt = new Map<string, number>();
  /** order id -> cumulative MQ already reported as fills; every source of order state funnels through this so nothing double-reports. */
  private cumSeen = new Map<string, MQ>();
  private lastOpenIds = new Set<string>();

  /** Virtual accounts are the app's, not the venue's; creation is delegated. */
  constructor(private readonly delegate: { createAccount: Engine['createAccount'] }) {
    this.feed.on('book', (b) => this.emitSnapshot(b.slug));
    this.feed.on('bbo', (q) => this.emitSnapshot(q.slug));
    this.feed.on('account', (a) => {
      for (const o of a.orders) this.orderSlugs.set(o.id, o.slug);
      this.reconcileFills(a.orders);
      for (const slug of this.subs.keys()) this.emitSnapshot(slug);
    });
    // Fills on resting orders arrive here, from the private stream.
    this.feed.on('execution', (ex) => this.onExecution(ex));
    void getMeta<Record<string, string>>(ORDER_ACCOUNTS_KEY, {}).then((saved) => {
      for (const [id, acct] of Object.entries(saved)) if (!this.orderAccounts.has(id)) this.orderAccounts.set(id, acct);
    }).catch((e) => logWarn('pm-engine', `order accounts not loaded: ${(e as Error).message}`));
  }

  /** Record which account an order belongs to, and save it: fills are attributed by order id alone. */
  private noteAccount(orderId: string, accountId: string): void {
    this.orderAccounts.set(orderId, accountId);
    if (this.saveAccountsTimer !== null) return;
    this.saveAccountsTimer = window.setTimeout(() => {
      this.saveAccountsTimer = null;
      const saved = Object.fromEntries([...this.orderAccounts].filter(([id]) => !id.startsWith('pending-')));
      setMeta(ORDER_ACCOUNTS_KEY, saved).catch((e) => logWarn('pm-engine', `order accounts not saved: ${(e as Error).message}`));
    }, 1_000);
  }

  /** A streamed execution: emits fill events and shrinks the queue estimate for shares that traded. */
  private onExecution(ex: PmExecution): void {
    const o = ex.order;
    if (/FILL/.test(ex.type) && ex.lastMq > 0) {
      // Report by cumulative quantity so the poll cannot report it again.
      const seen = this.cumSeen.get(o.id) ?? 0;
      const cum = Math.max(o.cumMq, seen + ex.lastMq);
      this.reportFill(o, cum, ex.lastMc, false);
      // Once we start filling, nothing is ahead of us at that level.
      this.estAhead.set(o.id, 0);
    }
    if (o.leavesMq <= 0 || /CANCELED|CANCELLED|REJECTED|EXPIRED|REPLACED/.test(o.state)) {
      this.estAhead.delete(o.id);
      this.orderAcceptedAt.delete(o.id);
    }
    logInfo('pm-engine', `${ex.type.toLowerCase()} ${o.id} ${o.slug}${ex.lastMq ? ` ${ex.lastMq / MQ_PER_CONTRACT} @ ${(ex.lastMc ?? 0) / 10}¢` : ''} · leaves ${o.leavesMq / MQ_PER_CONTRACT}`);
  }

  /** Book frames arrive only while the socket is open; before that the ladder is stale. */
  get live(): boolean {
    return this.feed.running && this.feed.wsStatus === 'open';
  }

  on<K extends keyof VenueEvents>(event: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn);
    return () => { set!.delete(fn); };
  }

  private emit<K extends keyof VenueEvents>(event: K, payload: VenueEvents[K]): void {
    for (const fn of this.listeners.get(event) ?? []) {
      try {
        fn(payload);
      } catch (e) {
        console.error('[pm-engine] listener error', e);
      }
    }
  }

  subscribe(ticker: string): void {
    this.subs.set(ticker, (this.subs.get(ticker) ?? 0) + 1);
    this.feed.subscribe(ticker);
    this.emitSnapshot(ticker);
  }

  releaseTicker(ticker: string): number {
    const n = (this.subs.get(ticker) ?? 0) - 1;
    if (n > 0) this.subs.set(ticker, n);
    else this.subs.delete(ticker);
    this.feed.release(ticker);
    return Math.max(0, n);
  }

  private emitSnapshot(slug: string): void {
    if (!this.subs.has(slug)) return;
    this.emit('snapshot', this.buildUiSnapshot(slug));
  }

  /** My live orders on a market, from the last account poll plus local edits. */
  private myOrders(slug: string): PmOrder[] {
    return this.feed.account.orders.filter((o) => o.slug === slug && o.priceMc !== null && o.leavesMq > 0
      && ACTIVE.has(ORDER_STATES[o.state] ?? 'resting'));
  }

  /** The panel's whole picture of one market, in the Kalshi engine's shape. */
  buildUiSnapshot(slug: string): UiSnapshot {
    const book = this.feed.book(slug) ?? null;
    const bbo = this.feed.cachedBbo(slug);
    const info = this.feed.marketInfo(slug);
    const acct = this.feed.account;
    const tickMc = info?.market.tickMc ?? inferTick(book);

    const bestBid = book?.bids[0]?.priceMc ?? bbo?.bidMc ?? null;
    const bestAsk = book?.offers[0]?.priceMc ?? bbo?.askMc ?? null;
    const bids = new Map((book?.bids ?? []).map((l) => [l.priceMc, l.mq]));
    const asks = new Map((book?.offers ?? []).map((l) => [l.priceMc, l.mq]));

    // Every order carries a YES price, so it keys straight onto the YES ladder.
    const mine = this.myOrders(slug);
    if (book) this.updateQueueEstimates(mine, bids, asks, book.t);
    const levels = new Map<MC, { yes: MQ; no: MQ; count: number; minQp: MQ | null }>();
    for (const o of mine) {
      const e = levels.get(o.priceMc!) ?? { yes: 0, no: 0, count: 0, minQp: null };
      if (ladderSide(o) === 'y') e.yes += o.leavesMq;
      else e.no += o.leavesMq;
      e.count++;
      const ahead = this.estAhead.get(o.id);
      if (ahead !== undefined && (e.minQp === null || ahead < e.minQp)) e.minQp = ahead;
      levels.set(o.priceMc!, e);
    }

    const rows: LadderRow[] = [];
    if (book) {
      for (let p = MC_SETTLE - tickMc; p > 0; p -= tickMc) {
        const e = levels.get(p);
        const side = !e ? '' : e.yes > 0 && e.no === 0 ? 'y' : e.no > 0 && e.yes === 0 ? 'n' : 'b';
        const queuePos = e && e.minQp !== null ? Math.round(e.minQp / MQ_PER_CONTRACT) : null;
        rows.push([e ? e.yes + e.no : 0, bids.get(p) ?? 0, p, asks.get(p) ?? 0, queuePos, e?.count ?? 0, side]);
      }
    }

    const pos = acct.positions.find((x) => x.slug === slug);
    const led = acct.ledger.get(slug);
    const orders: UiOrder[] = mine.map((o) => this.toUiOrder(o));
    // Unrealised from the venue's position; realised/fees from our ledger (venue drops flat positions, no realised figure).
    const pnl = pos || led ? (pos?.unrealizedCash ?? 0) + (led?.realizedCash ?? 0) - (led?.feeCash ?? 0) : null;

    return {
      type: 'snapshot',
      ticker: slug,
      stats: {
        bestYesBid: bestBid,
        bestYesAsk: bestAsk,
        midYesDeci: bestBid !== null && bestAsk !== null ? (bestBid + bestAsk) * 5 : null,
        openOrders: mine.length,
        bidOrders: mine.filter((o) => ladderSide(o) === 'y').length,
        askOrders: mine.filter((o) => ladderSide(o) === 'n').length,
        volume: Math.round((bbo?.sharesTradedMq ?? book?.sharesTradedMq ?? 0) / MQ_PER_CONTRACT),
        positionMq: pos?.netMq ?? led?.netMq ?? 0,
        pnlCash: pnl,
        tickMc,
        crossed: bestBid !== null && bestAsk !== null && bestBid >= bestAsk,
        marketStatus: marketStatus(book?.state ?? ''),
        marketResult: resultOf(book),
      },
      rows,
      orders,
      fills: this.fillMarkers(led),
      brackets: {},
      pnlByAccount: {},
    };
  }

  /** Today's fills by level, under the unattributed key: the venue has no accounts. */
  private fillMarkers(led: PmMarketLedger | undefined): Record<string, Record<number, [number, number]>> {
    if (!led) return {};
    const dayStart = new Date().setHours(0, 0, 0, 0);
    const byLevel: Record<number, [number, number]> = {};
    for (const f of led.fills) {
      if (f.t < dayStart) continue;
      const cell = byLevel[f.yesPriceMc] ?? (byLevel[f.yesPriceMc] = [0, 0]);
      cell[f.side === 'yes' ? 0 : 1] += 1;
    }
    return { '': byLevel };
  }

  /** Walk the queue estimate forward on a book frame; an order seen for the first time starts at the worst case. */
  private updateQueueEstimates(mine: PmOrder[], bids: Map<MC, MQ>, asks: Map<MC, MQ>, frameT: number): void {
    const live = new Set(mine.map((o) => o.id));
    for (const id of [...this.estAhead.keys()]) {
      if (!live.has(id)) {
        this.estAhead.delete(id);
        this.orderAcceptedAt.delete(id);
      }
    }
    const acceptedAt = (o: PmOrder): number => {
      let t = this.orderAcceptedAt.get(o.id);
      if (t === undefined) {
        t = Date.parse(o.createTime);
        if (!Number.isFinite(t)) t = 0;
        this.orderAcceptedAt.set(o.id, t);
      }
      return t;
    };
    // A frame stamped before placement doesn't yet contain the order; netting against it overcounts by one contract.
    const inFrame = (o: PmOrder) => frameT >= acceptedAt(o);
    const ours = new Map<string, MQ>();
    for (const o of mine) {
      if (!inFrame(o)) continue;
      const k = `${ladderSide(o)}:${o.priceMc}`;
      ours.set(k, (ours.get(k) ?? 0) + o.leavesMq);
    }
    for (const o of mine) {
      const cur = this.estAhead.get(o.id);
      if (!inFrame(o)) {
        // Too early to learn anything; a foreign order gets its worst case later.
        continue;
      }
      const side = ladderSide(o);
      const levelMq = (side === 'y' ? bids : asks).get(o.priceMc!) ?? 0;
      const others = Math.max(0, levelMq - (ours.get(`${side}:${o.priceMc}`) ?? 0));
      this.estAhead.set(o.id, cur === undefined ? others : Math.min(cur, others));
    }
  }

  /** Kalshi's UiOrder: `side` is the contract bought, `price_mc` its price. */
  private toUiOrder(o: PmOrder): UiOrder {
    const bidSide = ladderSide(o) === 'y';
    return {
      order_id: o.id,
      client_order_id: '',
      ticker: o.slug,
      status: ORDER_STATES[o.state] ?? 'resting',
      side: bidSide ? 'yes' : 'no',
      price_mc: bidSide ? o.priceMc : MC_SETTLE - o.priceMc!,
      remaining_mq: o.leavesMq,
      queue_position: this.estAhead.has(o.id) ? Math.round(this.estAhead.get(o.id)! / MQ_PER_CONTRACT) : null,
      created_time: o.createTime,
      source: this.orderAccounts.has(o.id) ? 'manual' : 'external',
      account_id: this.orderAccounts.get(o.id) ?? null,
    };
  }

  placeOrder: Engine['placeOrder'] = async (args) => {
    const t0 = performance.now();
    const res = await this.doPlace(args);
    journal({
      venue: 'polymarket', kind: 'place', tag: args.tag, ticker: args.ticker, orderId: res.ok ? res.order.order_id ?? undefined : undefined, bad: !res.ok,
      msg: `place ${args.mode} ${args.side} ${args.qtyMq / MQ_PER_CONTRACT} @ ${args.priceMc / 10}¢ ${args.ticker} → ${res.ok ? `${res.order.status} ${res.order.order_id ?? ''}` : `REFUSED: ${res.error}`} (${Math.round(performance.now() - t0)}ms)`,
      data: { args, result: res },
    });
    return res;
  };

  private doPlace: Engine['placeOrder'] = async (args) => {
    const { ticker, side, priceMc, qtyMq, mode, accountId } = args;
    if (qtyMq <= 0) return { ok: false, error: 'quantity must be > 0' };
    const yesPriceMc = side === 'yes' ? priceMc : MC_SETTLE - priceMc;
    if (yesPriceMc < 1 || yesPriceMc > 999) return { ok: false, error: `price out of range: ${yesPriceMc} mc` };
    const info = this.feed.marketInfo(ticker)?.market;
    const tick = info?.tickMc ?? inferTick(this.feed.book(ticker) ?? null);
    if (yesPriceMc % tick !== 0) {
      return { ok: false, error: `price ${(yesPriceMc / 10).toFixed(1)}¢ (YES) is not on this market's ${(tick / 10).toFixed(1)}¢ grid` };
    }
    if (!this.live) return { ok: false, error: 'Polymarket feed is not live — order not sent' };

    // Placeholder order lets the My column show it immediately; swapped for the real order (or dropped) once the venue answers.
    const provisionalId = mode === 'post' ? `pending-${++this.provisionalSeq}` : null;
    if (provisionalId) {
      this.orderAccounts.set(provisionalId, accountId);
      this.feed.editOrders((orders) => [...orders, {
        id: provisionalId, slug: ticker, side: 'buy', outcome: side, ladder: side === 'yes' ? 'y' : 'n',
        intent: side === 'yes' ? 'ORDER_INTENT_BUY_LONG' : 'ORDER_INTENT_BUY_SHORT', priceMc: yesPriceMc, qtyMq, leavesMq: qtyMq,
        cumMq: 0, avgMc: null, state: 'ORDER_STATE_PENDING_NEW', tif: 'TIME_IN_FORCE_GOOD_TILL_CANCEL', createTime: new Date().toISOString(),
      }]);
    }
    const dropProvisional = () => {
      if (!provisionalId) return;
      this.orderAccounts.delete(provisionalId);
      this.feed.editOrders((orders) => orders.filter((o) => o.id !== provisionalId));
    };

    let od: PmOrder;
    try {
      od = await this.feed.client.placeOrder({ slug: ticker, outcome: side, priceMc, qtyMq, mode, tag: args.tag });
    } catch (e) {
      dropProvisional();
      if (e instanceof AmbiguousPlacement) {
        // No client order id to resolve by: look for what we just sent.
        const found = await this.findRecentOrder(ticker, side, yesPriceMc, qtyMq);
        if (!found) {
          logError('pm-engine', `order ${ticker}: place ambiguous (${e.message}) and nothing matching is open — treating as NOT placed`);
          return { ok: false, error: `place ambiguous (${e.message}); nothing matching found — treating as NOT placed` };
        }
        logWarn('pm-engine', `order ${ticker}: place ambiguous (${e.message}) — matched open order ${found.id}`);
        od = found;
      } else {
        const err = e as VenueAPIError;
        logError('pm-engine', `order ${ticker}: ${err.message}`);
        return { ok: false, error: err.text || err.message, status: err.status };
      }
    }
    if (!od.id) {
      dropProvisional();
      logError('pm-engine', `order ${ticker}: place returned no order id`);
      return { ok: false, error: 'no order id in response' };
    }
    // Before any early return: an order that fills at once (every FOK hedge) still belongs to its account.
    this.noteAccount(od.id, accountId);
    let status = ORDER_STATES[od.state] ?? 'resting';
    // FOK never rests: if still pending after the sync window, poll briefly; either way it's kept out of the resting list.
    if (mode === 'fok' && /PENDING/.test(od.state)) {
      for (let i = 0; i < 4 && /PENDING/.test(od.state); i++) {
        await new Promise((r) => setTimeout(r, 250));
        try {
          od = await this.feed.client.getOrder(od.id);
        } catch { break; }
      }
      status = ORDER_STATES[od.state] ?? 'resting';
    }
    if (od.cumMq > 0) this.emitFill(ticker, accountId, side, od, mode === 'fok');
    if (status === 'canceled' || (mode === 'fok' && status !== 'filled')) {
      // A post-only that would have crossed, a rejection, or a kill: nothing rests.
      dropProvisional();
      const what = od.state.replace(/^ORDER_STATE_/, '').toLowerCase();
      logWarn('pm-engine', `order ${ticker}: ${what} by the venue${od.cumMq ? ` after ${od.cumMq / MQ_PER_CONTRACT} filled` : ''}`);
      void this.feed.pollAccount();
      if (od.cumMq > 0) {
        // Partially filled then killed: report the fill, nothing is out.
        return { ok: true, order: this.toNormalized(od, status === 'filled' ? 'executed' : 'canceled') };
      }
      return { ok: false, error: mode === 'fok' && status !== 'filled' ? `not filled — ${what}` : `order ${what} by Polymarket` };
    }
    if (status === 'filled') {
      // Crossed and done: positions move on the poll, no order is out.
      dropProvisional();
      logInfo('pm-engine', `order ${ticker}: ${od.id} filled ${od.cumMq / MQ_PER_CONTRACT} @ ${(od.avgMc ?? od.priceMc ?? 0) / 10}¢`);
      void this.feed.pollAccount();
      return { ok: true, order: this.toNormalized(od, 'executed') };
    }
    this.orderSlugs.set(od.id, ticker);
    // Everything resting at the level when ours arrived is ahead of it (from the frame before ours was added).
    // Keep the placeholder's estimate if it has one; the book now likely includes our own size.
    const book = this.feed.book(ticker);
    if (book && !(provisionalId && this.estAhead.has(provisionalId))) {
      const level = (side === 'yes' ? book.bids : book.offers).find((l) => l.priceMc === yesPriceMc);
      this.estAhead.set(od.id, level?.mq ?? 0);
    }
    // Venue time if it gave one, else now: frames stamped earlier predate us.
    const acceptedAt = Date.parse(od.createTime);
    this.orderAcceptedAt.set(od.id, Number.isFinite(acceptedAt) ? acceptedAt : Date.now());
    // Fill in what a thin response may omit, so the ladder shows it at once.
    if (od.priceMc === null) od.priceMc = yesPriceMc;
    if (!od.qtyMq) od.qtyMq = qtyMq;
    if (!od.leavesMq && status !== 'filled') od.leavesMq = qtyMq;
    if (!od.slug) od.slug = ticker;
    if (!od.createTime) od.createTime = new Date().toISOString();
    // The placeholder and the real order are the same row; keep one.
    if (provisionalId) {
      this.orderAccounts.delete(provisionalId);
      const est = this.estAhead.get(provisionalId);
      if (est !== undefined && !this.estAhead.has(od.id)) this.estAhead.set(od.id, est);
      this.estAhead.delete(provisionalId);
    }
    this.feed.editOrders((orders) => [...orders.filter((o) => o.id !== od.id && o.id !== provisionalId), od]);
    logInfo('pm-engine', `order ${ticker}: ${od.id} ${side} ${qtyMq / MQ_PER_CONTRACT} @ ${priceMc / 10}¢ ${status}`);
    return { ok: true, order: this.toNormalized(od, status) };
  };

  private toNormalized(od: PmOrder, status: string): NormalizedOrder {
    return {
      order_id: od.id,
      client_order_id: null,
      status,
      remaining_mq: od.leavesMq,
      fill_mq: od.cumMq || Math.max(0, od.qtyMq - od.leavesMq),
      yes_price_mc: od.priceMc,
      created_time: od.createTime,
      queue_position: null,
      raw: {},
    };
  }

  /** Report whatever cumulative fill hasn't been reported yet, as one fill event; stream, poll and read-back all funnel here. */
  private reportFill(o: PmOrder, cumMq: MQ, pxMc: MC | null, taker: boolean): void {
    const seen = this.cumSeen.get(o.id) ?? 0;
    const delta = cumMq - seen;
    if (delta <= 0) return;
    this.cumSeen.set(o.id, cumMq);
    const yesPx = pxMc ?? o.avgMc ?? o.priceMc;
    if (yesPx === null) return;
    const side = ladderSide(o) === 'y' ? 'yes' : 'no';
    journal({
      venue: 'polymarket', kind: 'fill', ticker: o.slug, orderId: o.id,
      msg: `FILL ${side} ${delta / MQ_PER_CONTRACT} @ ${yesPx / 10}¢ ${o.slug} ${taker ? 'taker' : 'maker'} · order ${o.id} · cum ${cumMq / MQ_PER_CONTRACT}`,
      data: { order: o, cumMq, seen, pxMc },
    });
    this.emit('fill', {
      type: 'fill_event', ticker: o.slug, account_id: this.orderAccounts.get(o.id) ?? '', ts: Math.floor(Date.now() / 1000),
      side, mq: delta, yes_price_mc: yesPx, is_taker: taker, order_id: o.id,
    });
  }

  private emitFill(ticker: string, accountId: string, side: 'yes' | 'no', od: PmOrder, taker: boolean): void {
    void ticker; void accountId; void side;
    this.reportFill(od, od.cumMq, od.avgMc, taker);
  }

  /** Report growth in any open order's fill, and read back any of ours that vanished, so no fill is missed. */
  private reconcileFills(open: PmOrder[]): void {
    const ids = new Set(open.map((o) => o.id));
    for (const o of open) if (o.cumMq > (this.cumSeen.get(o.id) ?? 0)) this.reportFill(o, o.cumMq, null, false);
    for (const id of this.lastOpenIds) {
      if (ids.has(id) || !this.orderAccounts.has(id) || id.startsWith('pending-')) continue;
      void this.feed.client.getOrder(id).then((od) => {
        if (od.cumMq > (this.cumSeen.get(id) ?? 0)) {
          logWarn('pm-engine', `${id} left the open list with ${od.cumMq / MQ_PER_CONTRACT} filled that no execution reported — reporting it now`);
          this.reportFill(od, od.cumMq, null, false);
        }
      }).catch(() => undefined);
    }
    this.lastOpenIds = ids;
  }

  /** Exchange truth for one order, read back by id. */
  async orderFill(orderId: string): Promise<{ filledMq: MQ; status: string } | null> {
    if (orderId.startsWith('pending-')) return null;
    const od = await this.feed.client.getOrder(orderId);
    return { filledMq: od.cumMq, status: ORDER_STATES[od.state] ?? 'resting' };
  }

  /** Teach the engine an order it did not place, so a cancel by id can route. */
  noteOrder(orderId: string, slug: string): void {
    this.orderSlugs.set(orderId, slug);
  }

  /** The most recent open order on this market that looks like the one we sent. */
  private async findRecentOrder(slug: string, side: 'yes' | 'no', yesPriceMc: MC, qtyMq: MQ): Promise<PmOrder | null> {
    let open: PmOrder[];
    try {
      open = await this.feed.client.getOpenOrders();
    } catch {
      return null;
    }
    const since = Date.now() - AMBIGUOUS_WINDOW_MS;
    const known = new Set(this.feed.account.orders.map((o) => o.id));
    const match = open.filter((o) => o.slug === slug && o.priceMc === yesPriceMc && o.qtyMq === qtyMq
      && o.ladder === (side === 'yes' ? 'y' : 'n')
      && !known.has(o.id)
      && (!o.createTime || Date.parse(o.createTime) >= since))
      .sort((a, b) => Date.parse(b.createTime) - Date.parse(a.createTime));
    return match[0] ?? null;
  }

  cancelOrder: Engine['cancelOrder'] = async (orderId, tag?: string) => {
    const t0 = performance.now();
    const res = await this.doCancel(orderId, tag);
    journal({
      venue: 'polymarket', kind: 'cancel', tag, orderId, ticker: this.orderSlugs.get(orderId), bad: !res.ok,
      msg: `cancel ${orderId} → ${res.ok ? 'ok' : `FAILED: ${res.error}`} (${Math.round(performance.now() - t0)}ms)`,
      data: { result: res },
    });
    return res;
  };

  private async doCancel(orderId: string, tag?: string): Promise<{ ok: boolean; error?: string; status?: number; filledMq?: MQ }> {
    if (orderId.startsWith('pending-')) return { ok: false, error: 'order is still being placed — try again in a moment' };
    const slug = this.orderSlugs.get(orderId) ?? this.feed.account.orders.find((o) => o.id === orderId)?.slug;
    if (!slug) return { ok: false, error: `unknown order ${orderId} — no market slug to cancel it on` };
    const t0 = performance.now();
    try {
      await this.feed.client.cancelOrder(orderId, slug, tag);
      logInfo('pm-engine', `cancelled ${orderId} in ${Math.round(performance.now() - t0)}ms`);
    } catch (e) {
      const err = e as VenueAPIError;
      // A 400 on cancel means the order is no longer open (filled or already cancelled); confirm before believing it.
      if (err.status === 400 || err.status === 404) {
        try {
          const cur = await this.feed.client.getOrder(orderId);
          if (!ACTIVE.has(ORDER_STATES[cur.state] ?? 'resting')) {
            this.feed.editOrders((orders) => orders.filter((o) => o.id !== orderId));
            // Gone is not the same as cancelled: say how much filled, so an arb quote's fill is hedged before it re-quotes.
            return { ok: true, filledMq: cur.cumMq };
          }
        } catch { /* fall through to the failure */ }
      }
      logError('pm-engine', `CANCEL FAILED for ${orderId} — order may still be resting: ${err.text || err.message}`);
      return { ok: false, error: err.text || err.message, status: err.status };
    }
    this.feed.editOrders((orders) => orders.filter((o) => o.id !== orderId));
    this.estAhead.delete(orderId);
    return { ok: true };
  }

  cancelLevel: Engine['cancelLevel'] = async (ticker, yesPriceMc, accountId) => {
    const ids = this.myOrders(ticker)
      .filter((o) => o.priceMc === yesPriceMc && this.ownedBy(o.id, accountId))
      .map((o) => o.id);
    return this.cancelMany(ids);
  };

  cancelAllOrders: Engine['cancelAllOrders'] = async (ticker, accountId) => {
    const ids = this.myOrders(ticker)
      .filter((o) => this.ownedBy(o.id, accountId))
      .map((o) => o.id);
    return this.cancelMany(ids);
  };

  /** Whether accountId may cancel this order; an order with no known account (placed elsewhere) is treated as everyone's. */
  private ownedBy(orderId: string, accountId?: string): boolean {
    if (!accountId) return true;
    const acct = this.orderAccounts.get(orderId);
    return acct === undefined || acct === accountId;
  }

  private async cancelMany(ids: string[]): Promise<{ ok: boolean; cancelled: number; failed: number; error?: string }> {
    if (!ids.length) return { ok: true, cancelled: 0, failed: 0 };
    const results = await Promise.all(ids.map((id) => this.cancelOrder(id)));
    const failed = results.filter((r) => !r.ok).length;
    const error = results.find((r) => !r.ok)?.error;
    return { ok: failed === 0, cancelled: ids.length - failed, failed, ...(error ? { error } : {}) };
  }

  /** Brackets hedge from the Kalshi engine's fill loop, which is not ported yet. */
  armBracket: Engine['armBracket'] = async () => ({ ok: false, error: 'brackets are not wired for Polymarket US yet' });
  createAccount: Engine['createAccount'] = (name) => this.delegate.createAccount(name);

  /** Our fills on a market, newest first, from the trade history. */
  recentFills: Engine['recentFills'] = async (ticker, _accountId, limit = 100): Promise<FillRow[]> => {
    const led = this.feed.account.ledger.get(ticker);
    if (!led) return [];
    return [...led.fills].sort((a, b) => b.t - a.t).slice(0, limit).map((f) => ({
      fill_id: f.fillId,
      ts: Math.floor(f.t / 1000),
      ticker: f.slug,
      side: f.side,
      mq: f.mq,
      yes_price_mc: f.yesPriceMc,
      fee_cash: f.feeCash,
      is_taker: f.isTaker,
      order_id: f.orderId,
      client_order_id: '',
      account_id: this.orderAccounts.get(f.orderId) ?? '',
    }));
  };

  private accountOf(orderId: string, known: Set<string>): string {
    const a = this.orderAccounts.get(orderId);
    return a && known.has(a) ? a : MOBILE_ID;
  }

  /** Display name for a market: its position or cached market title, else the slug. */
  titleOf(slug: string): string {
    return this.feed.account.positions.find((p) => p.slug === slug)?.title
      ?? this.feed.marketInfo(slug)?.market.title ?? slug;
  }

  /**
   * The same shape Kalshi's engine emits, split by app account. Fills go to the account their order was placed under;
   * anything unattributed, and any venue position the trade history does not explain, belongs to mobile.
   */
  buildPnlSnapshot(accounts: { id: string; name: string; parts: string[] }[]): PnlSnapshot {
    const a = this.feed.account;
    const known = new Set(accounts.map((x) => x.id));
    const ledgers = buildAccountLedgers(a.history.fills, a.history.resolutions, (f) => this.accountOf(f.orderId, known), MOBILE_ID);
    const venue = new Map(a.positions.map((p) => [p.slug, p]));

    // Share of each venue position the ledgers account for; the rest is mobile's.
    const ledgerNet = new Map<string, MQ>();
    for (const m of ledgers.values()) for (const l of m.values()) ledgerNet.set(l.slug, (ledgerNet.get(l.slug) ?? 0) + l.netMq);
    for (const p of a.positions) {
      const rest = p.netMq - (ledgerNet.get(p.slug) ?? 0);
      if (!rest) continue;
      let m = ledgers.get(MOBILE_ID);
      if (!m) ledgers.set(MOBILE_ID, (m = new Map()));
      const l = m.get(p.slug) ?? { slug: p.slug, netMq: 0, costCash: 0, realizedCash: 0, feeCash: 0, fills: [], lastT: 0 };
      l.netMq += rest;
      m.set(p.slug, l);
    }

    const rows: AccountRow[] = accounts.map((acct) => {
      const positions: PositionRow[] = [];
      for (const l of ledgers.get(acct.id)?.values() ?? []) {
        const p = venue.get(l.slug);
        // Unrealised is the venue's, split by each account's share of the position.
        const unreal = p && p.netMq && l.netMq ? Math.round((p.unrealizedCash * l.netMq) / p.netMq) : 0;
        if (!l.netMq && !l.realizedCash && !l.feeCash) continue;
        positions.push({
          ticker: l.slug,
          positionMq: l.netMq,
          avgCostMc: l.netMq && l.costCash ? l.costCash / l.netMq : null,
          midMc: p?.markMc ?? null,
          settled: l.settledT !== undefined,
          realizedCash: l.realizedCash,
          unrealizedCash: l.netMq ? unreal : null,
          feesCash: l.feeCash,
          pnlCash: l.realizedCash + unreal - l.feeCash,
          deployedCash: Math.abs(l.costCash),
        });
      }
      positions.sort((x, y) => x.ticker.localeCompare(y.ticker));
      const sum = (f: (p: PositionRow) => number) => positions.reduce((s, p) => s + f(p), 0);
      return {
        id: acct.id, name: acct.name, parts: acct.parts,
        realizedCash: sum((p) => p.realizedCash), unrealizedCash: sum((p) => p.unrealizedCash ?? 0), feesCash: sum((p) => p.feesCash),
        pnlCash: sum((p) => p.pnlCash), deployedCash: sum((p) => p.deployedCash), positionMq: sum((p) => p.positionMq), positions,
      };
    });

    const names = new Map(accounts.map((x) => [x.id, x.name]));
    const grp = new Map<string, OrdersOutRow>();
    for (const o of a.orders) {
      if (!o.leavesMq || o.id.startsWith('pending-')) continue;
      const acct = this.accountOf(o.id, known);
      const key = `${o.slug}\u0000${acct}`;
      const d = grp.get(key) ?? { ticker: o.slug, account_id: acct, account: names.get(acct) ?? acct, bids: 0, asks: 0, bracket: 0 };
      if (ladderSide(o) === 'y') d.bids++;
      else d.asks++;
      grp.set(key, d);
    }
    const ordersOut = [...grp.values()].sort((x, y) => x.ticker.localeCompare(y.ticker) || x.account.localeCompare(y.account));

    // Settled and closed markets across every account, one row per market as on Kalshi.
    const cutoff = Date.now() - 24 * 3600 * 1000;
    const settledRecent: SettledRow[] = [];
    const settled: SettledRow[] = [];
    for (const [slug, l] of a.ledger) {
      const pnl = l.realizedCash - l.feeCash;
      const row: SettledRow = {
        ticker: this.titleOf(slug), result: l.settledT !== undefined ? 'settled' : 'closed', pnlCash: pnl,
        settled_time: l.settledT !== undefined ? new Date(l.settledT).toISOString().slice(0, 10) : '',
      };
      if (l.settledT !== undefined) (l.settledT >= cutoff ? settledRecent : settled).push(row);
      else if (!l.netMq && (venue.get(slug)?.netMq ?? 0) === 0 && (l.realizedCash || l.feeCash)) settledRecent.push(row);
    }

    return {
      type: 'pnl_update',
      balanceCash: a.balance?.balanceCash ?? 0,
      totalPnlCash: rows.reduce((s, r) => s + r.pnlCash, 0),
      accounts: rows,
      tree: pnlTree(rows),
      orders_out: ordersOut,
      settled_recent: settledRecent,
      settled,
    };
  }

  /** Every fill in the trade history grouped by account, newest first. */
  fillsByAccount(accounts: { id: string }[]): Map<string, FillRow[]> {
    const known = new Set(accounts.map((x) => x.id));
    const out = new Map<string, FillRow[]>(accounts.map((x) => [x.id, []]));
    for (const f of [...this.feed.account.history.fills].sort((x, y) => y.t - x.t)) {
      const acct = this.accountOf(f.orderId, known);
      const list = out.get(acct) ?? [];
      list.push({
        fill_id: f.fillId, ts: Math.floor(f.t / 1000), ticker: f.slug, side: f.side, mq: f.mq, yes_price_mc: f.yesPriceMc,
        fee_cash: f.feeCash, is_taker: f.isTaker, order_id: f.orderId, client_order_id: '', account_id: acct,
      });
      out.set(acct, list);
    }
    return out;
  }

  tradeHistory: Engine['tradeHistory'] = async () => ({ trades: [], exhausted: true, hitCap: false });
  priceBars: Engine['priceBars'] = async () => [];
  bidAskHistory: Engine['bidAskHistory'] = async () => [];
  volumeProfile: Engine['volumeProfile'] = async () => ({ byPrice: new Map<MC, MQ>(), trades: 0, complete: true });

  /** Ladder-bar quick jump: markets across the matching events, lines first. */
  searchMarkets: Engine['searchMarkets'] = async (q) => {
    if (q.trim().length < 2) return [];
    let events: PmEvent[];
    try {
      events = await this.feed.searchEvents(q);
    } catch {
      return [];
    }
    return events.flatMap((e) => e.markets.map((m) => ({ e, m })))
      .sort((a, b) => (KIND_ORDER[a.m.sportsType] ?? 5) - (KIND_ORDER[b.m.sportsType] ?? 5))
      .slice(0, 12)
      .map(({ e, m }) => ({ ticker: m.slug, title: `${e.title} · ${m.title}`, status: 'open' }));
  };

  exploreState(): ExploreState {
    return { building: false, count: null };
  }

  async searchEvents(q: string, limit = 8): Promise<{ hits: ExploreEvent[]; total: number }> {
    const events = await this.feed.searchEvents(q, limit);
    const hits = events.map((e) => ({
      id: e.slug,
      title: e.title + (e.live ? ' · LIVE' : ''),
      tag: e.tags[0] || e.category,
      sub: `${e.startTime ? `${when(e.startTime)} · ` : ''}${e.markets.length.toLocaleString()} market${e.markets.length === 1 ? '' : 's'}`,
    }));
    return { hits, total: hits.length };
  }

  async eventMarkets(eventId: string): Promise<ExploreMarket[]> {
    let e = this.feed.eventBySlug(eventId);
    if (!e) {
      const found = await this.feed.searchEvents(eventId, 1);
      e = found[0] ?? null;
    }
    if (!e) return [];
    return [...e.markets]
      .sort((a, b) => (KIND_ORDER[a.sportsType] ?? 5) - (KIND_ORDER[b.sportsType] ?? 5) || a.title.localeCompare(b.title))
      .map((m) => toExploreMarket(m, this.feed.cachedBbo(m.slug)));
  }

  /** Volume is not in the listing; it comes from the BBO, cached per market. */
  async marketVolume(ticker: string): Promise<MQ | null> {
    const b = await this.feed.bbo(ticker);
    return b ? b.sharesTradedMq : null;
  }
}

function toExploreMarket(m: PmMarket, bbo: PmBbo | null): ExploreMarket {
  return {
    ticker: m.slug,
    title: m.question && m.question !== m.title ? `${m.title} — ${m.question}` : m.title,
    bidMc: m.bestBidMc,
    askMc: m.bestAskMc,
    volume: bbo ? bbo.sharesTradedMq / MQ_PER_CONTRACT : null,
    status: m.closed ? 'settled' : 'open',
    kind: m.sportsType && m.sportsType !== 'FUTURE' ? m.sportsType.toLowerCase().replace('_', ' ') : '',
  };
}

/** 'yes' / 'no' once the market has settled, from the YES side's settlement price; '' while open or unknown. */
export function resultOf(book: PmBook | null): '' | 'yes' | 'no' {
  if (!book || marketStatus(book.state) !== 'settled' || book.settlementMc === null) return '';
  if (book.settlementMc >= MC_SETTLE - 10) return 'yes';
  if (book.settlementMc <= 10) return 'no';
  return '';
}

function marketStatus(state: string): string {
  const s = state.replace(/^MARKET_STATE_/, '').toLowerCase();
  if (s === 'expired' || s === 'terminated') return 'settled';
  return s || 'open';
}

/** "Thu 8:35 PM" for a game start, local time; '' if unparseable. */
function when(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

/** Infers the price grid (1¢, 0.5¢, or 0.1¢ tick) from the GCD of quoted levels when the market record doesn't say. */
export function inferTick(b: PmBook | null): number {
  if (!b) return 10;
  const px = [...b.bids, ...b.offers].map((l) => l.priceMc).filter((p) => p > 0);
  if (px.length < 2) return 10;
  const gcd = (a: number, c: number): number => (c === 0 ? a : gcd(c, a % c));
  const g = px.reduce((acc, p) => gcd(acc, p), 0);
  if (g % 10 === 0) return 10;
  if (g % 5 === 0) return 5;
  return 1;
}
